import apiRouteMapping from '@/features/app/constants/api-route-mapping';
import useAppSelector from '@/features/app/hooks/useAppSelector';
import isAccessTokenStillValid from '@/features/auth/functions/isAccessTokenStillValid';
import useCanClientUseCookieTokenStorage from '@/features/auth/hooks/useCanClientUseCookieTokenStorage';
import axios, { AxiosError, isCancel } from 'axios';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFeatureFlags from '@/features/feature-flags/hooks/useFeatureFlags';

export default function useAxios() {
  const { i18n } = useTranslation();
  const featureFlags = useFeatureFlags();
  const { token } = useAppSelector((state) => state.auth);
  const canClientUseCookieStorage = useCanClientUseCookieTokenStorage();

  return useMemo(() => {
    const instance = axios.create({
      baseURL: import.meta.env.REACT_APP_API_URL,
      headers: {
        Authorization:
          !canClientUseCookieStorage && featureFlags.auth.useLegacyTokenStorage
            ? `Bearer ${token?.value}`
            : undefined,
        'Content-Type': 'application/json',
      },
      withCredentials: canClientUseCookieStorage ? true : undefined,
    });

    instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (
          error.response &&
          error.response.status === 401 &&
          error.request?.responseURL &&
          // verify user endpoint should not trigger 401 event
          !new URL((error.request?.responseURL ?? '') as string).pathname.startsWith(
            `${apiRouteMapping['/users/verify']}`
          )
        ) {
          window.dispatchEvent(new Event('app:apiCallReturns401'));
          return Promise.reject(error);
        } else {
          if (isCancel(error)) {
            // custom logging logic
            return Promise.reject(error);
          }
          return Promise.reject(error);
        }
      }
    );

    instance.interceptors.request.use((config) => {
      const abortController = new AbortController();

      // since in other places, token is set to expired after the threshold is reached, but that means that the token is still valid
      // and we don't want to abort the request
      const tokenStillValid = isAccessTokenStillValid(
        featureFlags.auth.useCookieTokenStorage ?? false,
        featureFlags.auth.useLegacyTokenStorage ?? false,
        0
      );

      if (!tokenStillValid) {
        abortController.abort();
      }

      return {
        ...config,
        params: {
          ...config.params,
          lang: i18n.language,
        },
        signal: abortController.signal,
      };
    });

    return instance;
  }, [
    canClientUseCookieStorage,
    featureFlags.auth.useLegacyTokenStorage,
    featureFlags.auth.useCookieTokenStorage,
    token?.value,
    i18n.language,
  ]);
}
